import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
    LayoutDashboard,
    Settings,
    LogOut,
    Tv2,
    Wallet,
    MonitorPlay,
    Store,
    UserPlus,
    UserCheck,
    Monitor,
    MessageSquare,
    type LucideIcon,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

interface NavItem {
    title: string;
    path: string;
    icon: LucideIcon;
}

interface NavSection {
    label?: string;
    items: NavItem[];
}

const getMenuSections = (role: string | undefined, platforms: string[] = []): NavSection[] => {
    if (role === "admin") {
        return [
            { items: [{ title: "Dashboard", path: "/dashboard", icon: LayoutDashboard }] },
            {
                label: "2ONAMEDIA",
                items: [
                    { title: "Activar Usuarios", path: "/admin/activar-usuarios", icon: UserCheck },
                    { title: "Distribuidores", path: "/admin/distribuidores", icon: Store },
                    { title: "Vendedores", path: "/resellers", icon: UserPlus },
                    { title: "Usuarios", path: "/admin/usuarios", icon: Monitor },
                ],
            },
            {
                label: "XUPER PLAY",
                items: [{ title: "Xuper Play", path: "/admin/xuper-play", icon: MonitorPlay }],
            },
            {
                label: "Contenido",
                items: [{ title: "Plantillas", path: "/templates", icon: MessageSquare }],
            },
        ];
    }
    if (role === "distribuidor" || role === "reseller") {
        const isDist = role === "distribuidor";
        const items: NavSection[] = [];
        items.push({
            items: [
                {
                    title: "Inicio",
                    path: isDist ? "/distribuidor/dashboard" : "/reseller/dashboard",
                    icon: LayoutDashboard,
                },
            ],
        });

        const platformItems: NavItem[] = [];
        if (platforms.includes("2onamedia")) {
            platformItems.push({
                title: "2ONA MEDIA",
                path: isDist ? "/distribuidor/clientes/2onamedia" : "/reseller/users/2onamedia",
                icon: Tv2,
            });
        }
        if (platforms.includes("onetv")) {
            platformItems.push({
                title: "Xuper Play",
                path: isDist ? "/distribuidor/clientes/onetv" : "/reseller/users/onetv",
                icon: MonitorPlay,
            });
        }
        if (isDist) {
            platformItems.unshift({
                title: "Mis Vendedores",
                path: "/distribuidor/resellers",
                icon: UserPlus,
            });
        }
        if (platformItems.length > 0) {
            items.push({ label: "Clientes", items: platformItems });
        }

        items.push({
            label: "Finanzas",
            items: [{ title: "Wallet", path: isDist ? "/distribuidor/wallet" : "/reseller/wallet", icon: Wallet }],
        });
        items.push({
            label: "Cuenta",
            items: [
                {
                    title: "Plantillas",
                    path: isDist ? "/distribuidor/templates" : "/reseller/templates",
                    icon: MessageSquare,
                },
                {
                    title: "Configuración",
                    path: isDist ? "/distribuidor/settings" : "/reseller/settings",
                    icon: Settings,
                },
            ],
        });
        return items;
    }
    return [];
};

interface SidebarProps {
    isCollapsed: boolean;
    toggleSidebar: () => void;
}

export default function Sidebar({ isCollapsed, toggleSidebar }: SidebarProps) {
    const { user, logout } = useAuth();
    const location = useLocation();
    const sections = getMenuSections(user?.role, user?.platforms_enabled || []);

    const handleMenuItemClick = () => {
        if (window.innerWidth < 1024 && !isCollapsed) toggleSidebar();
    };

    const handleLogout = async () => {
        try {
            localStorage.clear();
            sessionStorage.clear();
            await logout();
            window.location.replace("/login");
        } catch {
            window.location.replace("/login");
        }
    };

    const isItemActive = (item: NavItem) => {
        if (item.path === "/dashboard" || item.path === "/reseller/dashboard" || item.path === "/distribuidor/dashboard") {
            return location.pathname === item.path;
        }
        return location.pathname === item.path || location.pathname.startsWith(item.path);
    };

    return (
        <>
            {!isCollapsed && (
                <div
                    className="fixed inset-0 bg-[#0f1729]/60 backdrop-blur-md z-40 lg:hidden transition-all duration-300"
                    onClick={toggleSidebar}
                />
            )}

            <aside
                className={`h-screen flex flex-col transition-all duration-300 ease-in-out z-50
                    ${isCollapsed
                        ? "-translate-x-full lg:translate-x-0 lg:w-[72px]"
                        : "translate-x-0 w-64"
                    }
                    fixed lg:relative
                    border-r border-white/[0.08]
                `}
                style={{
                    background: "linear-gradient(180deg, rgba(15,23,41,0.92) 0%, rgba(12,19,34,0.96) 100%)",
                    backdropFilter: "blur(14px)",
                    WebkitBackdropFilter: "blur(14px)",
                }}
            >
                {/* Logo */}
                <div className={`flex items-center ${isCollapsed ? "justify-center py-4 px-2" : "px-5 py-4"}`}>
                    <img
                        src="/360xs256-logo.png"
                        alt="360XS"
                        className={`transition-all duration-300 object-contain ${
                            isCollapsed ? "w-9 h-9" : "w-28 h-auto"
                        }`}
                        style={{ filter: "drop-shadow(0 0 10px rgba(139,92,246,0.45))" }}
                    />
                    {!isCollapsed && (
                        <button
                            onClick={toggleSidebar}
                            className="lg:hidden ml-auto p-1.5 rounded-lg text-white/30 hover:text-white/70 hover:bg-white/5 transition-colors"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M15 18l-6-6 6-6" />
                            </svg>
                        </button>
                    )}
                </div>

                {/* Nav */}
                <nav className="flex-1 px-2.5 py-2 space-y-1 overflow-y-auto">
                    {sections.map((section, si) => (
                        <div key={section.label ?? `section-${si}`} className="mb-1">
                            {!isCollapsed && section.label && (
                                <p className="px-3 pt-4 pb-1.5 text-[10px] font-semibold text-white/25 uppercase tracking-[0.14em]">
                                    {section.label}
                                </p>
                            )}
                            {isCollapsed && section.label && si !== 0 && (
                                <div className="mx-3 my-2 h-px bg-white/[0.06]" />
                            )}
                            <div className="space-y-0.5">
                                {section.items.map((item) => {
                                    const active = isItemActive(item);
                                    return (
                                        <Link
                                            key={item.title + item.path}
                                            to={item.path}
                                            onClick={handleMenuItemClick}
                                            title={isCollapsed ? item.title : undefined}
                                            className={`
                                                group relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150
                                                ${active
                                                    ? "bg-violet-500/12 text-white border border-violet-500/25 shadow-[0_0_18px_-3px_rgba(139,92,246,0.25)]"
                                                    : "text-white/55 hover:text-white/90 hover:bg-white/[0.06] border border-transparent"
                                                }
                                                ${isCollapsed ? "justify-center" : ""}
                                            `}
                                        >
                                            {/* Barra de indicación */}
                                            <span
                                                className={`absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-r-full transition-all duration-200 ${
                                                    active ? "h-5 bg-gradient-to-b from-violet-400 to-blue-500 shadow-[0_0_8px_rgba(139,92,246,0.8)]" : "h-0"
                                                }`}
                                            />
                                            <item.icon
                                                className={`shrink-0 transition-colors ${
                                                    active ? "text-violet-300" : "text-white/50 group-hover:text-white/80"
                                                }`}
                                                size={18}
                                            />
                                            {!isCollapsed && (
                                                <span className="text-[13px] font-medium tracking-wide truncate">
                                                    {item.title}
                                                </span>
                                            )}
                                            {!isCollapsed && active && (
                                                <span className="ml-auto w-1.5 h-1.5 rounded-full bg-violet-400 shrink-0 shadow-[0_0_8px_rgba(139,92,246,0.9)]" />
                                            )}
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </nav>

                {/* Footer: user info + logout */}
                <div className="border-t border-white/[0.08] p-2.5">
                    {!isCollapsed && user && (
                        <div className="flex items-center gap-3 px-2.5 py-2 mb-1">
                            <div className="relative shrink-0">
                                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-violet-600 to-blue-700 flex items-center justify-center text-white text-[11px] font-bold">
                                    {(user.full_name || user.email || 'A').slice(0, 2).toUpperCase()}
                                </div>
                                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-[#0c1322]" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-[12px] font-medium text-white/80 truncate">{user.full_name || 'Admin'}</p>
                                <p className="text-[10px] text-white/40 truncate">{user.email}</p>
                            </div>
                        </div>
                    )}
                    <button
                        onClick={handleLogout}
                        title={isCollapsed ? "Cerrar sesión" : undefined}
                        className={`
                            w-full flex items-center gap-3 px-3 py-2.5 rounded-xl
                            text-white/45 hover:text-red-400 hover:bg-red-500/10
                            transition-all duration-150 group
                            ${isCollapsed ? "justify-center" : ""}
                        `}
                    >
                        <LogOut size={16} className="shrink-0 transition-colors" />
                        {!isCollapsed && (
                            <span className="text-[13px] font-medium">Cerrar Sesión</span>
                        )}
                    </button>
                </div>
            </aside>
        </>
    );
}